import React from 'react';
import {
  Box, Container, Grid, Typography, Card, CardContent,
  Button, Divider,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BusinessIcon from '@mui/icons-material/Business';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import { useLang } from '../context/LanguageContext';

const icons = [BusinessIcon, MedicalServicesIcon, HomeRepairServiceIcon, ElectricalServicesIcon, PlumbingIcon, AcUnitIcon];


export default function Services() {
  const { t } = useLang();
  const s = t.services;

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box id="services" sx={{ py: { xs: 7, md: 11 }, background: '#FAFAFA' }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box mb={{ xs: 5, md: 7 }} sx={{ textAlign: { xs: 'left', md: 'center' } }}>
          <Box display="flex" alignItems="center" gap={2} mb={2} justifyContent={{ xs: 'flex-start', md: 'center' }}>
            <Box sx={{ width: 40, height: 3, background: '#C8102E', flexShrink: 0 }} />
            <Typography sx={{
              fontFamily: '"Oswald", sans-serif',
              color: '#C8102E',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              fontSize: '0.78rem',
              fontWeight: 500,
            }}>
              {s.eyebrow}
            </Typography>
            <Box sx={{ width: 40, height: 3, background: '#C8102E', flexShrink: 0, display: { xs: 'none', md: 'block' } }} />
          </Box>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.6rem' }, mb: 2 }}>
            {s.heading}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 560, lineHeight: 1.8, fontWeight: 300, mx: { md: 'auto' } }}>
            {s.subtext}
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 2, md: 3 }}>
          {s.items.map((service, idx) => {
            const Icon = icons[idx];
            return (
              <Grid item xs={12} sm={6} lg={4} key={service.title}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #E8E8E8',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: '#C8102E',
                      transform: 'scaleX(0)',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover::after': { transform: 'scaleX(1)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 3, md: 4 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        background: '#111111',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2.5,
                        transition: 'background 0.3s ease',
                        '.MuiCard-root:hover &': { background: '#C8102E' },
                      }}
                    >
                      <Icon sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>

                    <Typography variant="h5" sx={{ mb: 1.5, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                      {service.title}
                    </Typography>

                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.8, mb: 3, flexGrow: 1, fontSize: '0.88rem' }}>
                      {service.description}
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={0.8} mb={3}>
                      {service.tags.map((tag) => (
                        <Box key={tag} sx={{
                          px: 1.2,
                          py: 0.3,
                          background: '#F0F0F0',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: '#555555',
                        }}>
                          {tag}
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ mb: 2.5 }} />

                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon />}
                      onClick={scrollToContact}
                      sx={{
                        p: 0,
                        color: '#111111',
                        fontFamily: '"Oswald", sans-serif',
                        fontWeight: 500,
                        letterSpacing: '0.08em',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        '&:hover': { color: '#C8102E', background: 'transparent' },
                        alignSelf: 'flex-start',
                      }}
                    >
                      {s.cta}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom CTA */}
        <Box
          mt={{ xs: 6, md: 8 }}
          p={{ xs: 4, md: 5 }}
          sx={{
            background: '#111111',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ color: '#fff', mb: 0.5, fontSize: { xs: '1.1rem', md: '1.35rem' } }}>
              {s.bottomCta.heading}
            </Typography>
            <Typography variant="body2" sx={{ color: '#888888' }}>
              {s.bottomCta.sub}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={scrollToContact}
            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {s.bottomCta.btn}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
